import * as React from 'react'
import { useEffect, useMemo } from 'react'
import { StackScreenProps } from '@react-navigation/stack'
import { AppNavigatorParams } from '../../../navigation/types'
import { useTranslation } from 'react-i18next'
import { ScreenWrapper } from '../../../@ui/containers/grid/screen.wrapper'
import Row from '../../../@ui/containers/grid/row'
import { MelddSearchBar } from '../../../@ui/form/meldd-search.input'
import { useMyNodesQuery } from '../../../generated/graphql'
import Column from '../../../@ui/containers/grid/column'
import { PaginatedFlatList, Dropdown } from '../../../@ui'
import { Node } from '../../../generated/graphql'
import { View } from 'react-native'
import { Button, IconButton, Text } from 'react-native-paper'
import { SortOptions, SortField, SortDirection } from '../../../@ui/buttons'
import { IconAdd } from '../../../utils/meldd-icons'
import { useAuthContext } from '../../../contexts/auth-context'
import { useFocusEffect } from '@react-navigation/native'
import { useIsMobileDevice } from '../../../hooks/is-mobile.hook'
import { useNode } from '../../../contexts/current-node.provider'
import NodeCard from './component/ node.card'

export function MyNodesScreen({
  navigation,
}: StackScreenProps<AppNavigatorParams, 'MyNodes'>) {
  const { t } = useTranslation('nodes')
  const { t: tTiers } = useTranslation('tiers')
  const isMobile = useIsMobileDevice()
  const { currentNode: node } = useNode()
  const [searchQuery, setSearchQuery] = React.useState('')
  const [isPrivateFilter, setIsPrivateFilter] = React.useState<
    boolean | undefined
  >(undefined)
  const [paymentStatusFilter, setPaymentStatusFilter] = React.useState<
    string | undefined
  >(undefined)
  const [sortField, setSortField] = React.useState<SortField>('createdAt')
  const [sortDirection, setSortDirection] =
    React.useState<SortDirection>('DESC')
  const { authAction } = useAuthContext()
  const { data, fetchMore, refetch } = useMyNodesQuery({
    variables: {
      input: {
        offset: 0,
        limit: 10,
        name: searchQuery,
        isPrivate: isPrivateFilter,
        sortBy:
          sortField === 'name'
            ? 'name'
            : sortField === 'createdAt'
            ? 'createdAt'
            : undefined,
        sort:
          sortField === 'name' || sortField === 'createdAt'
            ? sortDirection === 'ASC'
              ? 'asc'
              : 'desc'
            : undefined,
      },
    },
  })

  useFocusEffect(
    React.useCallback(() => {
      refetch()
    }, [refetch])
  )

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      refetch()
    }, 500) // 500ms debounce delay

    return () => clearTimeout(timeoutId)
  }, [searchQuery, refetch])

  // Refetch when filters change
  useEffect(() => {
    refetch()
  }, [isPrivateFilter, refetch])

  // Refetch when sort changes (backend sorting)
  useEffect(() => {
    refetch()
  }, [sortField, sortDirection, refetch])

  // Filter and sort nodes
  const filteredAndSortedNodes = useMemo(() => {
    if (!data?.myNodes?.results) {
      return []
    }

    let nodes = [...data.myNodes.results]

    // Filter by payment status
    if (paymentStatusFilter) {
      nodes = nodes.filter((node) => {
        if (paymentStatusFilter === 'payment-required') {
          return !node.activeTier
        }
        const status = node.activeTier?.status?.toLowerCase()
        const filterStatus = paymentStatusFilter.toLowerCase()

        if (filterStatus === 'active') {
          return status === 'active'
        }
        if (filterStatus === 'cancelled') {
          return status === 'cancelled'
        }
        if (filterStatus === 'pending_renovation') {
          return status === 'pending_renovation'
        }
        return false
      })
    }

    return nodes
  }, [data?.myNodes?.results, paymentStatusFilter, sortField, sortDirection])

  return (
    <ScreenWrapper style={{ padding: 10 }} withScrollView={false}>
      <Column gap={4} flex={1}>
        <Row columnGap={4} wrap>
          <IconButton
            mode={'outlined'}
            icon="arrow-left"
            onPress={() =>
              node
                ? navigation.push('ClusterList', { nodeId: node.id })
                : navigation.push('ListNodes')
            }
          />
          <MelddSearchBar
            placeholder={t('Search my nodes')}
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {!isMobile ? (
            <Button
              icon={IconAdd}
              mode="elevated"
              onPress={() => {
                authAction(navigation, () =>
                  navigation.navigate('NodeSettings', { nodeId: '' })
                )
              }}
            >
              {t('Create Node')}
            </Button>
          ) : (
            <IconButton
              mode="contained"
              icon={IconAdd}
              onPress={() =>
                authAction(navigation, () =>
                  navigation.navigate('NodeSettings', { nodeId: '' })
                )
              }
            />
          )}
        </Row>
        <Row
          columnGap={4}
          wrap
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Row alignItems="center" columnGap={4}>
            <View style={{ minWidth: 150, flex: isMobile ? 1 : 0 }}>
              <Column gap={2}>
                <Text variant="labelMedium">{t('filter-by-visibility')}</Text>
                <Dropdown
                  items={[
                    { label: t('all'), id: 'all' },
                    { label: t('public'), id: 'public' },
                    { label: t('private'), id: 'private' },
                  ]}
                  selected={
                    isPrivateFilter === undefined
                      ? t('all')
                      : isPrivateFilter
                      ? t('private')
                      : t('public')
                  }
                  showSearch={false}
                  onSelect={(item) => {
                    if (item.id === 'all') {
                      setIsPrivateFilter(undefined)
                    } else {
                      setIsPrivateFilter(item.id === 'private')
                    }
                  }}
                />
              </Column>
            </View>
            <View
              style={{
                minWidth: 150,
                flex: isMobile ? 1 : 0,
              }}
            >
              <Column gap={2}>
                <Text variant="labelMedium">
                  {t('filter-by-payment-status')}
                </Text>
                <Dropdown
                  items={[
                    { label: t('all'), id: 'all' },
                    { label: tTiers('active'), id: 'active' },
                    { label: tTiers('cancelled'), id: 'cancelled' },
                    {
                      label: tTiers('pending-renovation'),
                      id: 'pending_renovation',
                    },
                    {
                      label: tTiers('payment-required'),
                      id: 'payment-required',
                    },
                  ]}
                  selected={
                    paymentStatusFilter
                      ? paymentStatusFilter === 'payment-required'
                        ? tTiers('payment-required')
                        : paymentStatusFilter === 'pending_renovation'
                        ? tTiers('pending-renovation')
                        : paymentStatusFilter === 'cancelled'
                        ? tTiers('cancelled')
                        : tTiers('active')
                      : t('all')
                  }
                  showSearch={false}
                  onSelect={(item) => {
                    if (item.id === 'all') {
                      setPaymentStatusFilter(undefined)
                    } else {
                      setPaymentStatusFilter(item.id)
                    }
                  }}
                />
              </Column>
            </View>
          </Row>
          <View style={{ flexShrink: 0 }}>
            <Column gap={2}>
              <SortOptions
                options={[
                  {
                    field: 'createdAt',
                    direction: 'DESC',
                    label: t('sort-by-created-desc'),
                    icon: 'sort-calendar-descending',
                  },
                  {
                    field: 'createdAt',
                    direction: 'ASC',
                    label: t('sort-by-created-asc'),
                    icon: 'sort-calendar-ascending',
                  },
                  {
                    field: 'name',
                    direction: 'ASC',
                    label: t('sort-by-name-asc'),
                    icon: 'sort-alphabetical-ascending',
                  },
                  {
                    field: 'name',
                    direction: 'DESC',
                    label: t('sort-by-name-desc'),
                    icon: 'sort-alphabetical-descending',
                  },
                ]}
                selectedField={sortField}
                selectedDirection={sortDirection}
                onSortChange={(field, direction) => {
                  setSortField(field)
                  setSortDirection(direction)
                }}
              />
            </Column>
          </View>
        </Row>
        <PaginatedFlatList
          data={filteredAndSortedNodes}
          total={
            paymentStatusFilter
              ? filteredAndSortedNodes.length
              : data?.myNodes?.total || 0
          }
          onLoadMore={async (params: any) => {
            // Only load more if we're not filtering by payment status on frontend
            if (!paymentStatusFilter) {
              await fetchMore({
                variables: {
                  input: {
                    ...params,
                    name: searchQuery,
                    isPrivate: isPrivateFilter,
                    sortBy:
                      sortField === 'name'
                        ? 'name'
                        : sortField === 'createdAt'
                        ? 'createdAt'
                        : undefined,
                    sort:
                      sortField === 'name' || sortField === 'createdAt'
                        ? sortDirection === 'ASC'
                          ? 'asc'
                          : 'desc'
                        : undefined,
                  },
                },
              })
            }
          }}
          onRefresh={async () => {
            await refetch()
          }}
          renderItem={({ item }: any) => {
            const node = item as Node
            return <NodeCard node={node} />
          }}
          contentContainerStyle={{ flexGrow: 1 }}
        />
      </Column>
    </ScreenWrapper>
  )
}
